import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { debounce } from 'lodash';
import { useQuery } from '@apollo/client/react';
import { FeatureKey, PlaceFindByIdDocument } from '@gql';
import { ListingsArray } from '../types';
import { scrollToTop } from '@utils/scroll-to-top';
import { useMap } from '@components/map/map-context';
import { useSearch } from '@contexts/search-context';
import { ListingPartial } from './listing-map-card';
import { MIN_ACCEPTABLE_ZOOM, MAP_CHANGE_DEBOUNCE_MS, FITBOUNDS_PADDING } from './constants';
const SESSION_KEYS = {
  scrollPosition: 'searchResultsScrollPosition',
  isListActive: 'isListActive',
  selectedListingId: 'selectedListingId'
} as const;
function isNewPageNavigation(): boolean {
  const entries = window.performance.getEntriesByType('navigation');
  if (entries.length === 0) return false;
  return (entries[0] as PerformanceNavigationTiming).type === 'navigate';
}
function useScrollPosition(containerRef, isListActive) {
  const $ = _c(12);
  const [, setSavedPosition] = React.useState(0);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = position => {
      setSavedPosition(position);
      sessionStorage.setItem(SESSION_KEYS.scrollPosition, position.toString());
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const savePosition = t0;
  let t1;
  let t2;
  if ($[1] !== containerRef) {
    t1 = () => {
      const container = containerRef.current;
      if (!container) {
        return;
      }
      const handleScroll = () => {
        const position_0 = container.scrollTop;
        if (position_0 > 0) {
          savePosition(position_0);
        }
      };
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    };
    t2 = [containerRef, savePosition];
    $[1] = containerRef;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  React.useEffect(t1, t2);
  let t3;
  let t4;
  if ($[4] !== containerRef || $[5] !== isListActive) {
    t3 = () => {
      if (isListActive && containerRef.current) {
        const savedPos = sessionStorage.getItem(SESSION_KEYS.scrollPosition);
        if (savedPos) {
          const position_1 = parseInt(savedPos);
          if (position_1 > 0) {
            requestAnimationFrame(() => {
              if (containerRef.current && position_1 <= containerRef.current.scrollHeight) {
                containerRef.current.scrollTop = position_1;
              }
            });
          }
        }
      }
    };
    t4 = [isListActive, containerRef];
    $[4] = containerRef;
    $[5] = isListActive;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  React.useEffect(t3, t4);
  let t5;
  if ($[8] !== containerRef) {
    t5 = () => {
      if (isNewPageNavigation()) {
        sessionStorage.removeItem(SESSION_KEYS.scrollPosition);
        setSavedPosition(0);
        if (containerRef.current) {
          containerRef.current.scrollTop = 0;
        }
      }
    };
    $[8] = containerRef;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [];
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  React.useEffect(t5, t6);
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      savePosition,
      setSavedPosition
    };
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  return t7;
}
export function useSearchMapState() {
  const search = useSearch();
  const {
    map
  } = useMap();
  const {
    activeFilters,
    listingsLoading,
    listingsQueryResults,
    listingsError,
    retrySearch,
    placeId,
    placeName: _placeName,
    geoSearchBox,
    resetPagination,
    setActiveFilters,
    setCheckInDate,
    setCheckOutDate,
    setMapGeoSearchBox,
    markMapViewportAsManuallyChanged,
    mapViewportManuallyChanged,
    propertyTypes,
    setPropertyTypes,
    minNumberOfRooms,
    setMinNumberOfRooms,
    minNumberOfBathrooms,
    setMinNumberOfBathrooms,
    minNumberOfBeds,
    setMinNumberOfBeds,
    minPrice,
    maxPrice,
    setMinPrice,
    setMaxPrice
  } = search;

  // Refs
  const resultsContainerRef = React.useRef<HTMLDivElement>(null);

  // Core UI state
  const [isListActive, setIsListActive] = React.useState<boolean>(true);
  const [isContentReady, setIsContentReady] = React.useState(false);

  // Listing selection state
  const [hoveredListingSlug, setHoveredListingSlug] = React.useState<string | undefined>(undefined);
  const [selectedListingId, setSelectedListingId] = React.useState<string | undefined>(undefined);
  const [selectedListing, setSelectedListing] = React.useState<ListingPartial | undefined>(undefined);

  // Map zoom state
  const [zoomingOutToNewListing, setZoomingOutToNewListing] = React.useState(false);
  const [preZoomMapListingCount, setPreZoomMapListingCount] = React.useState(0);
  const [isAboveMinZoom, setIsAboveMinZoom] = React.useState(false);

  // fitBounds management: apply once when geoSearchBox first becomes available,
  // then suppress the resulting zoom_changed re-search.
  const hasFittedBoundsRef = React.useRef(false);
  const suppressNextMapChangeRef = React.useRef(false);
  const [mapListings, setMapListings] = React.useState<ListingsArray>([]);
  const placeName = mapViewportManuallyChanged ? undefined : _placeName || undefined;

  // Country detection
  const {
    data: placeData
  } = useQuery(PlaceFindByIdDocument, {
    variables: {
      id: placeId || ''
    },
    skip: !placeId
  });
  const [searchedCountry, setSearchedCountry] = React.useState<string | undefined>(undefined);

  // Scroll position management
  const {
    savePosition,
    setSavedPosition
  } = useScrollPosition(resultsContainerRef, isListActive);

  // -- Derived values --

  const memoizedListingEntries = React.useMemo(() => {
    if (listingsQueryResults?.listingsSearch.__typename === 'ListingSearchCursorCollection') {
      return listingsQueryResults.listingsSearch.entries;
    }
    return [];
  }, [listingsQueryResults]);
  const shouldShowContent = isContentReady;
  const shouldShowLoadingState = !shouldShowContent;
  const listingEntries = listingsQueryResults?.listingsSearch.__typename === 'ListingSearchCursorCollection' && listingsQueryResults?.listingsSearch.entries || [];
  const hasOnlyPetFriendlyFilter = activeFilters.length === 1 && activeFilters[0].key === FeatureKey.PetFriendly;
  const hasFiltersApplied = activeFilters.length > 0 && !hasOnlyPetFriendlyFilter || minNumberOfBathrooms > 0 || minNumberOfBeds > 0 || minNumberOfRooms > 0 || propertyTypes.length > 0 || !!minPrice || !!maxPrice;
  const isValidationError = listingsQueryResults?.listingsSearch.__typename === 'ValidationErrorCollection';

  // -- Handlers --

  const saveScrollPosition = React.useCallback(() => {
    const container = resultsContainerRef.current;
    if (container) {
      savePosition(container.scrollTop);
    }
  }, [savePosition]);
  const clearDates = React.useCallback((): void => {
    setCheckInDate(null);
    setCheckOutDate(null);
  }, [setCheckInDate, setCheckOutDate]);
  const resetFilters = React.useCallback((): void => {
    setPropertyTypes([]);
    setActiveFilters([]);
    setMinNumberOfRooms(0);
    setMinNumberOfBathrooms(0);
    setMinNumberOfBeds(0);
    setMinPrice(null);
    setMaxPrice(null);
  }, [setPropertyTypes, setActiveFilters, setMinNumberOfRooms, setMinNumberOfBathrooms, setMinNumberOfBeds, setMinPrice, setMaxPrice]);
  const onMapCardClose = React.useCallback(() => {
    setSelectedListingId(undefined);
  }, []);
  const onMapClick = React.useCallback((_event: google.maps.MapMouseEvent) => {
    setSelectedListingId(undefined);
  }, []);
  const updateSelectedListing = React.useCallback((listing: ListingPartial, isFavorited: boolean) => {
    setSelectedListing({
      ...listing,
      isFavorited
    });
  }, []);
  const syncMapZoom = React.useCallback((map: google.maps.Map) => {
    const zoom = map.getZoom();
    setIsAboveMinZoom((zoom || MIN_ACCEPTABLE_ZOOM) > MIN_ACCEPTABLE_ZOOM);
    if (zoom) {
      const queryParams = new URLSearchParams(window.location.search);
      queryParams.set('zoom', zoom.toString());
      history.replaceState(null, '', '?' + queryParams.toString());
    }
  }, []);
  const mapChangeHandler = React.useCallback((map: google.maps.Map) => {
    const bounds = map.getBounds();
    syncMapZoom(map);
    const centerPointLat = bounds?.getCenter().lat();
    const centerPointLng = bounds?.getCenter().lng();
    const southWestPointLat = bounds?.getSouthWest().lat();
    const southWestPointLng = bounds?.getSouthWest().lng();
    const northEastPointLat = bounds?.getNorthEast().lat();
    const northEastPointLng = bounds?.getNorthEast().lng();
    const hasAllCoordinates = centerPointLat != null && centerPointLng != null && southWestPointLat != null && southWestPointLng != null && northEastPointLat != null && northEastPointLng != null;
    if (hasAllCoordinates) {
      resetPagination();
      scrollToTop();
      setMapGeoSearchBox({
        southWestPoint: {
          lat: southWestPointLat,
          lng: southWestPointLng
        },
        northEastPoint: {
          lat: northEastPointLat,
          lng: northEastPointLng
        },
        centerPoint: {
          lat: centerPointLat,
          lng: centerPointLng
        }
      });
    }
  }, [syncMapZoom, resetPagination, setMapGeoSearchBox]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedMapChangeHandler = React.useCallback(debounce((map: google.maps.Map) => mapChangeHandler(map), MAP_CHANGE_DEBOUNCE_MS), []);
  const onMapDragEnd = React.useCallback((map: google.maps.Map) => {
    markMapViewportAsManuallyChanged();
    debouncedMapChangeHandler(map);
  }, [markMapViewportAsManuallyChanged, debouncedMapChangeHandler]);

  // The initial listing search already covers the fitted area, so the
  // zoom_changed that fitBounds fires only records the new zoom.
  const onMapZoomChanged = React.useCallback((map: google.maps.Map) => {
    if (suppressNextMapChangeRef.current) {
      syncMapZoom(map);
      return;
    }
    markMapViewportAsManuallyChanged();
    debouncedMapChangeHandler(map);
  }, [syncMapZoom, markMapViewportAsManuallyChanged, debouncedMapChangeHandler]);
  const toggleListView = React.useCallback(() => {
    if (isListActive && resultsContainerRef.current) {
      savePosition(resultsContainerRef.current.scrollTop);
    }
    setIsListActive(!isListActive);
  }, [isListActive, savePosition]);
  const zoomOutToNewListing = React.useCallback((previousListingCount: number) => {
    if (listingsLoading) return;
    const currentZoom = map?.getZoom();
    const canZoomOut = currentZoom && currentZoom > MIN_ACCEPTABLE_ZOOM;
    const newListingsFound = listingEntries.length > previousListingCount;
    if (canZoomOut && !newListingsFound) {
      map?.setZoom(currentZoom - 1);
    } else {
      setZoomingOutToNewListing(false);
    }
  }, [map, listingEntries, listingsLoading]);

  // -- Effects --

  // Fit map bounds when geoSearchBox first becomes available (from URL coords
  // or from PlaceFindById geometry resolution). Padding ensures markers near
  // the edges aren't clipped. The suppression flag skips the zoom_changed the
  // fit fires and is released once the fit settles on idle. A fit that keeps
  // the zoom fires no zoom_changed, so the flag cannot rely on one to clear it.
  React.useEffect(() => {
    if (!map || !geoSearchBox || hasFittedBoundsRef.current) return;
    hasFittedBoundsRef.current = true;
    suppressNextMapChangeRef.current = true;
    const ne = new google.maps.LatLng(geoSearchBox.northEastPoint.lat, geoSearchBox.northEastPoint.lng);
    const sw = new google.maps.LatLng(geoSearchBox.southWestPoint.lat, geoSearchBox.southWestPoint.lng);
    google.maps.event.addListenerOnce(map, 'idle', () => {
      suppressNextMapChangeRef.current = false;
    });
    map.fitBounds(new google.maps.LatLngBounds(sw, ne), FITBOUNDS_PADDING);
  }, [map, geoSearchBox]);

  // Track content readiness
  React.useEffect(() => {
    if (!listingsLoading && listingsQueryResults) {
      setIsContentReady(true);
    } else if (listingsLoading) {
      setIsContentReady(false);
    }
  }, [listingsLoading, listingsQueryResults]);

  // Restore state from session storage
  React.useEffect(() => {
    const savedIsListActive = sessionStorage.getItem(SESSION_KEYS.isListActive);
    const savedSelectedListingId = sessionStorage.getItem(SESSION_KEYS.selectedListingId);
    const savedScrollPos = sessionStorage.getItem(SESSION_KEYS.scrollPosition);
    if (savedIsListActive) {
      setIsListActive(JSON.parse(savedIsListActive));
    }
    if (savedSelectedListingId) {
      setSelectedListingId(savedSelectedListingId);
    }
    if (savedScrollPos) {
      setSavedPosition(parseInt(savedScrollPos));
    }
  }, [listingsLoading, listingsQueryResults]);

  // Handle state persistence and scroll restoration
  React.useEffect(() => {
    const container = resultsContainerRef.current;
    if (!container || !isContentReady) return;
    if (isNewPageNavigation()) {
      sessionStorage.removeItem(SESSION_KEYS.scrollPosition);
      sessionStorage.removeItem(SESSION_KEYS.isListActive);
      sessionStorage.removeItem(SESSION_KEYS.selectedListingId);
      container.scrollTop = 0;
      setIsListActive(true);
      setSelectedListingId(undefined);
    } else {
      const savedPosition = sessionStorage.getItem(SESSION_KEYS.scrollPosition);
      if (savedPosition) {
        const position = parseInt(savedPosition);
        if (position >= 0) {
          requestAnimationFrame(() => {
            if (container && position <= container.scrollHeight) {
              container.scrollTop = position;
            }
          });
        }
      }
    }
  }, [isContentReady]);

  // Sync selected listing from ID
  React.useEffect(() => {
    if (selectedListingId) {
      const listing = memoizedListingEntries.find(l => l.id === selectedListingId);
      setSelectedListing(listing);
      sessionStorage.setItem(SESSION_KEYS.selectedListingId, selectedListingId);
    } else {
      setSelectedListing(undefined);
      sessionStorage.removeItem(SESSION_KEYS.selectedListingId);
    }
  }, [selectedListingId, memoizedListingEntries]);

  // Persist list active state
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(SESSION_KEYS.isListActive, isListActive.toString());
    }
  }, [isListActive]);

  // Track zoom-out-to-new-listing progress
  React.useEffect(() => {
    if (listingsLoading) return;
    const newListingsFound = memoizedListingEntries.length > preZoomMapListingCount;
    if (!newListingsFound && zoomingOutToNewListing) {
      zoomOutToNewListing(preZoomMapListingCount);
    } else {
      setPreZoomMapListingCount(memoizedListingEntries.length);
      setZoomingOutToNewListing(false);
    }
  }, [memoizedListingEntries, listingsLoading, zoomingOutToNewListing, preZoomMapListingCount]);

  // Track if map is above minimum zoom
  React.useEffect(() => {
    setIsAboveMinZoom((map?.getZoom() || MIN_ACCEPTABLE_ZOOM) > MIN_ACCEPTABLE_ZOOM);
  }, [map]);

  // Sync searched country
  React.useEffect(() => {
    if (placeData?.placeById?.__typename === 'Place') {
      setSearchedCountry(placeData.placeById.countryOrRegionShortName);
    }
  }, [placeData?.placeById]);

  // Sync map listings from query results
  React.useEffect(() => {
    if (listingsQueryResults?.listingsSearch?.__typename === 'ListingSearchCursorCollection') {
      setMapListings(listingsQueryResults.listingsSearch.entries);
      if (selectedListing && !listingsQueryResults.listingsSearch.entries.map(listing => listing.id).includes(selectedListing.id)) {
        setSelectedListingId(undefined);
      }
    }
  }, [listingsQueryResults]);
  return {
    // Search context pass-through
    search,
    map,
    // Refs
    resultsContainerRef,
    // UI state
    isListActive,
    isContentReady,
    shouldShowContent,
    shouldShowLoadingState,
    // Listing state
    hoveredListingSlug,
    setHoveredListingSlug,
    selectedListingId,
    setSelectedListingId,
    selectedListing,
    updateSelectedListing,
    // Map state
    isAboveMinZoom,
    zoomingOutToNewListing,
    setZoomingOutToNewListing,
    mapListings,
    placeName,
    searchedCountry,
    // Derived
    listingEntries,
    hasFiltersApplied,
    isValidationError,
    listingsError,
    retrySearch,
    isGeoResolutionError: search.isGeoResolutionError,
    // Handlers
    saveScrollPosition,
    clearDates,
    resetFilters,
    onMapCardClose,
    onMapClick,
    debouncedMapChangeHandler,
    onMapDragEnd,
    onMapZoomChanged,
    toggleListView,
    savePosition
  };
}